import { useToast } from "@qubit/autoparts";
import { useCallback } from "react";

import { useAppSelector } from "~/app/store";
import useFlag from "~/config/flags";
import { useUnifySubscription } from "~/lib/signalr";
import {
  selectThisWorkstation,
  selectWorkstationAllPorts
} from "~/redux/selectors/workstationsSelectors";

import { UnifyEvent } from "~/types/api";

const ESTOP_PORT_ERRORS = [
  "ESTOP_SWITCH",
  "SF_ESTOP_ACTIVATED",
  "SF_ESTOP_LOOP_ACTIVATED",
  "SF_EXTERNAL_ESTOP_ACTIVATED",
  "SF_EXTERNAL_ESTOP_LOOP_ACTIVATED"
];

export const useUnifyConnect = () => {
  const { enableUnifyConnectToasts } = useFlag();
  const { warningToast } = useToast();

  const workstation = useAppSelector(selectThisWorkstation);
  const allWorkstationPorts = useAppSelector(selectWorkstationAllPorts);
  const handleUnifyEvent = useCallback(
    (event: UnifyEvent) => {
      if (
        !enableUnifyConnectToasts ||
        !workstation ||
        event.case !== "PortErrorV1" ||
        event.item.gridId !== workstation.autostoreGridId ||
        !allWorkstationPorts.includes(event.item.portId) ||
        (event.item.multiPortId &&
          !allWorkstationPorts.includes(event.item.multiPortId))
      )
        return;

      const targetPortId = event.item.multiPortId ?? event.item.portId;

      if (event.item.portError === "OVERFILLED_BIN_DETECTED") {
        warningToast(
          `Bin over height, please remove or re-arrange the content and hit the retry button at port ${targetPortId}`
        );
      } else if (ESTOP_PORT_ERRORS.includes(event.item.portError)) {
        warningToast(
          `E-Stop has been activated, please release the E-Stop and press retry at port ${targetPortId}`
        );
      } else if (event.item.portError === "BELT_MOVE_TIMEOUT") {
        warningToast(
          `Bin may be overweight, please remove product before trying to reinduct at port ${targetPortId}`
        );
      } else if (event.item.portError === "DRIVER_UNIT_DISCONNECTED") {
        warningToast(
          `Port Lost Power, please hit the power button at port ${targetPortId}`
        );
      }
    },
    [allWorkstationPorts, enableUnifyConnectToasts, warningToast, workstation]
  );

  useUnifySubscription(handleUnifyEvent);
};
